import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [stale, setStale] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = () => api.get('/notifications')
    .then(ns => { setNotifications(ns); setStale(false); })
    .catch(() => setStale(true));

  useEffect(() => {
    load();
    let intervalId = setInterval(load, 30000);

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(intervalId);
      } else {
        load();
        intervalId = setInterval(load, 30000);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const unread = notifications.filter(n => !n.read);

  // Only reflect "read" locally when the write actually succeeded. Swallowing the
  // error and updating anyway cleared the bell while the server still had them
  // unread, so they reappeared on the next load with nothing explaining why.
  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(ns => ns.map(n => ({ ...n, read: true })));
    } catch { /* leave them unread — the next load reflects the server */ }
  };

  const handleClick = async (n) => {
    if (!n.read) {
      try {
        await api.put(`/notifications/${n.id}/read`);
        setNotifications(ns => ns.map(x => x.id === n.id ? { ...x, read: true } : x));
      } catch { /* as above — navigation below still happens either way */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative flex items-center justify-center w-8 h-8 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
      >
        <svg className={`w-4 h-4 ${stale ? 'opacity-40' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" title={stale ? 'Notifications may be out of date' : undefined}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center leading-none">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-3 top-14 md:absolute md:inset-x-auto md:right-0 md:top-full md:mt-2 md:w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Notifications</span>
            {unread.length > 0 && (
              <button onClick={markAllRead} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700">
            {notifications.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-8">No notifications</p>
            ) : notifications.map(n => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                  !n.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />}
                  <div className={!n.read ? '' : 'ml-3.5'}>
                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{n.title}</p>
                    {n.body && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
